import { useState } from "react"

import useDateFormatters from "@hooks/useDateFormatters"
import useHasAccess from "@hooks/useHasAccess"

import { NO_REFERENCE_SENTINEL, findBrandSubscription, findCatalogSubscription } from "@utils/catalogSubscriptions"
import { faIcon } from "@utils/faIcon"

import { Bullet, Skeleton } from "@particles/index"

import { Avatar, IconButton, Tooltip } from "@atoms/index"

import AlertBadge from "@molecules/AlertBadge/AlertBadge"
import { DisplayModuleWithAlert, DisplayModuleWithoutAlert } from "@molecules/DisplayModule/DisplayModule.types"
import { DisplayModule, EmptyState, Prompt } from "@molecules/index"

import { MenuContentNode } from "@organisms/Menu/Menu.types"
import { Menu } from "@organisms/index"

import {
    useBrandCountsQuery,
    useCatalogBrandsQuery,
    useCatalogResyncMutation,
    useCatalogSubscriptionsQuery,
    useSubscribeMutation,
    useSubscriptionPreviewCountMutation,
    useUnsubscribeMutation,
} from "@pages/Settings/SettingsCatalogs/SettingsCatalogs.store"
import CatalogBrandRow from "@pages/Settings/SettingsCatalogs/components/CatalogBrandRow/CatalogBrandRow"

import { PERMISSION_LEVEL } from "@constants/permissionLevel"

import styles from "./CatalogCard.module.scss"
import { CatalogCardProps } from "./CatalogCard.types"

function getCoverageCaption(subscribedItemCount: number, itemCount: number, isCatalogSubscribed: boolean): string {
    const itemNoun = itemCount === 1 ? "item" : "items"

    if (itemCount === 0) {
        return "No items yet"
    } else if (isCatalogSubscribed) {
        return `All ${itemCount.toLocaleString()} ${itemNoun} subscribed`
    } else if (subscribedItemCount === 0) {
        return `${itemCount.toLocaleString()} ${itemNoun}`
    } else {
        return `${subscribedItemCount.toLocaleString()} of ${itemCount.toLocaleString()} ${itemNoun} subscribed`
    }
}

export default function CatalogCard(props: CatalogCardProps) {
    const { catalog } = props

    const { formatDatetime } = useDateFormatters()
    const { hasPermission } = useHasAccess()
    const hasPricebookEditPermission = hasPermission("pricebook_edit_permission", PERMISSION_LEVEL.FULL)

    const brandsQuery = useCatalogBrandsQuery(catalog.id)
    const brandCountsQuery = useBrandCountsQuery(catalog.id)
    const subscriptionsQuery = useCatalogSubscriptionsQuery(catalog.id)

    const previewCountMutation = useSubscriptionPreviewCountMutation()
    const subscribeMutation = useSubscribeMutation()
    const unsubscribeMutation = useUnsubscribeMutation()
    const resyncMutation = useCatalogResyncMutation()

    const [isSubscribePromptOpen, setIsSubscribePromptOpen] = useState(false)
    const [isUnsubscribePromptOpen, setIsUnsubscribePromptOpen] = useState(false)

    const isSyncing = catalog.last_sync_status === "in_progress"
    const isSyncFailed = catalog.last_sync_status === "failed"

    // A finished sync stamps the completion time whether it succeeded or failed, so a
    // missing timestamp only ever means the catalog has never been synced.
    const lastSyncedAt = formatDatetime(catalog.last_sync_completed_at, "long")
    const syncCaption = lastSyncedAt ? `Last synced ${lastSyncedAt}` : "Never synced"

    const subscriptions = subscriptionsQuery.data ?? []
    const catalogSubscription = findCatalogSubscription(subscriptions)
    const isCatalogSubscribed = catalogSubscription !== null

    const brandCounts = brandCountsQuery.data ?? []
    const countByBrand = new Map(brandCounts.map((brandCount) => [brandCount.brand, brandCount]))
    const itemCount = brandCounts.reduce((total, brandCount) => total + brandCount.item_count, 0)
    const subscribedItemCount = brandCounts.reduce((total, brandCount) => total + brandCount.subscribed_item_count, 0)

    // The brand listing carries only real brands; the brandless "No Brand" bucket is a client-side
    // pseudo-row (a null brand) appended when the counts report active items with no brand.
    const brandRows: Array<CatalogBrand | null> = [...(brandsQuery.data ?? [])]
    if (countByBrand.has(NO_REFERENCE_SENTINEL)) {
        brandRows.push(null)
    }

    const isBrandListLoading = brandsQuery.isLoading || brandCountsQuery.isLoading || subscriptionsQuery.isLoading

    const handleSubscribeClick = () => {
        setIsSubscribePromptOpen(true)
        previewCountMutation.mutate({ catalog: catalog.id, level: "catalog" })
    }

    const handleSubscribeConfirm = () => {
        setIsSubscribePromptOpen(false)
        subscribeMutation.mutate({ catalog: catalog.id, level: "catalog" })
    }

    const handleUnsubscribeConfirm = () => {
        setIsUnsubscribePromptOpen(false)
        if (catalogSubscription !== null) {
            unsubscribeMutation.mutate(catalogSubscription)
        }
    }

    const isSubscriptionActionPending = subscribeMutation.isPending || unsubscribeMutation.isPending
    const isResyncPending = isSyncing || resyncMutation.isPending

    const menuItems: MenuContentNode[] = isCatalogSubscribed
        ? [
              {
                  type: "group",
                  items: [
                      {
                          variant: "danger",
                          title: "Unsubscribe from catalog",
                          icon: faIcon({ name: "trash-can", style: "regular" }),
                          onClick: () => setIsUnsubscribePromptOpen(true),
                      },
                  ],
              },
          ]
        : [
              {
                  type: "group",
                  items: [
                      {
                          title: "Subscribe to catalog",
                          icon: faIcon({ name: "circle-plus", style: "regular" }),
                          onClick: handleSubscribeClick,
                      },
                  ],
              },
          ]

    // A failed sync is the module's own alert: the ring and banner carry the failure, and the
    // banner's action is the one thing that clears it.
    const alertProps:
        | Pick<DisplayModuleWithAlert, "showAlert" | "alertBannerStatus" | "alertBanner">
        | Pick<DisplayModuleWithoutAlert, "showAlert"> = isSyncFailed
        ? {
              showAlert: true,
              alertBannerStatus: "error",
              alertBanner: {
                  message: `The last sync failed on ${lastSyncedAt}. Items and prices may be out of date.`,
                  cta: hasPricebookEditPermission
                      ? {
                            children: "Resync",
                            onClick: () => resyncMutation.mutate(catalog),
                            isDisabled: isResyncPending,
                        }
                      : undefined,
              },
          }
        : { showAlert: false }

    const previewCount = previewCountMutation.data?.count
    const ongoingImportClause = " — new items added to this catalog later will be imported automatically too"
    const subscribeBody =
        previewCount === undefined
            ? `This will import this catalog's items into your pricebook and keep them in sync${ongoingImportClause}. Are you sure you want to do this?`
            : `This will import ${previewCount} ${previewCount === 1 ? "item" : "items"} into your pricebook and keep them in sync${ongoingImportClause}. Are you sure you want to do this?`

    const renderBrandList = () => {
        if (isBrandListLoading) {
            return (
                <>
                    <Skeleton height={56} />
                    <Skeleton height={56} />
                    <Skeleton height={56} />
                </>
            )
        }

        if (brandsQuery.isError || brandCountsQuery.isError || subscriptionsQuery.isError) {
            return (
                <EmptyState
                    variant="error"
                    icon={faIcon({ name: "circle-xmark", style: "solid" })}
                    title="Hmm, something went wrong..."
                    supportText="An error occurred while fetching this catalog's brands."
                    primaryCTA={{
                        children: "Try again",
                        onClick: () => {
                            void brandsQuery.refetch()
                            void brandCountsQuery.refetch()
                            void subscriptionsQuery.refetch()
                        },
                    }}
                />
            )
        }

        if (brandRows.length === 0) {
            return (
                <EmptyState
                    variant="default"
                    icon={faIcon({ name: "shelves", style: "solid" })}
                    title="No brands synced yet"
                    supportText="Resync the catalog to pull in the store's brands and items."
                    primaryCTA={
                        hasPricebookEditPermission
                            ? {
                                  children: "Resync catalog",
                                  onClick: () => resyncMutation.mutate(catalog),
                                  isProcessing: resyncMutation.isPending,
                                  isDisabled: isResyncPending,
                              }
                            : undefined
                    }
                />
            )
        }

        return brandRows.map((brand) => {
            const brandId = brand?.id ?? null
            const count = countByBrand.get(brandId ?? NO_REFERENCE_SENTINEL)
            const brandSubscription = findBrandSubscription(subscriptions, brandId)
            const hasFullItemCoverage =
                count !== undefined && count.item_count > 0 && count.subscribed_item_count >= count.item_count
            const isFullySubscribed = isCatalogSubscribed || brandSubscription !== null || hasFullItemCoverage

            return (
                <CatalogBrandRow
                    key={brandId ?? "no-brand"}
                    catalogId={catalog.id}
                    brand={brand}
                    count={count}
                    brandSubscription={brandSubscription}
                    isFullySubscribed={isFullySubscribed}
                />
            )
        })
    }

    return (
        <>
            <DisplayModule
                className={styles.base}
                avatar={
                    <Avatar
                        variant="company"
                        size="lg"
                        src={catalog.logo_url ?? undefined}
                        fallbackName={catalog.name}
                    />
                }
                label={catalog.name}
                caption={
                    <>
                        {catalog.platform_display}
                        {brandCountsQuery.data !== undefined && (
                            <>
                                {" "}
                                <Bullet /> {getCoverageCaption(subscribedItemCount, itemCount, isCatalogSubscribed)}
                            </>
                        )}
                    </>
                }
                titleRight={
                    isCatalogSubscribed ? (
                        <AlertBadge status="success" size="sm" showIcon={true} isLoading={false}>
                            Subscribed
                        </AlertBadge>
                    ) : undefined
                }
                headerAction1={
                    hasPricebookEditPermission ? (
                        <Tooltip body={isSyncing ? "Sync in progress…" : `Resync\n${syncCaption}`}>
                            <IconButton
                                variant="ghost"
                                size="md"
                                icon={faIcon({ name: "arrows-rotate", style: "regular" })}
                                aria-label="Resync catalog"
                                isProcessing={isResyncPending}
                                isDisabled={isResyncPending}
                                onClick={() => resyncMutation.mutate(catalog)}
                            />
                        </Tooltip>
                    ) : undefined
                }
                headerAction2={
                    hasPricebookEditPermission ? (
                        <Menu
                            popoverSide="bottom"
                            popoverAlign="end"
                            title={catalog.name}
                            trigger={
                                <IconButton
                                    variant="ghost"
                                    size="md"
                                    icon={faIcon({ name: "ellipsis", style: "regular" })}
                                    aria-label="More options"
                                    isProcessing={isSubscriptionActionPending}
                                    isDisabled={isSubscriptionActionPending}
                                />
                            }
                            items={menuItems}
                        />
                    ) : undefined
                }
                contentClassName={styles.brands}
                {...alertProps}
            >
                {renderBrandList()}
            </DisplayModule>

            <Prompt
                title={`Subscribe to ${catalog.name}?`}
                body={subscribeBody}
                isOpen={isSubscribePromptOpen}
                isLoading={previewCountMutation.isPending}
                confirmText="Subscribe"
                onConfirm={handleSubscribeConfirm}
                cancelText="Cancel"
                onCancel={() => setIsSubscribePromptOpen(false)}
            />

            <Prompt
                title={`Unsubscribe from ${catalog.name}?`}
                body="Items already in your pricebook will stay there, but they will no longer receive catalog updates, and new catalog items will no longer be imported automatically."
                isOpen={isUnsubscribePromptOpen}
                confirmText="Unsubscribe"
                onConfirm={handleUnsubscribeConfirm}
                cancelText="Cancel"
                onCancel={() => setIsUnsubscribePromptOpen(false)}
                isDangerousOperation={true}
            />
        </>
    )
}
